import { useId } from 'react';
import type { RecipeAccent } from '../data/recipes';

type RecipeIllustrationProps = {
  accent: RecipeAccent;
  variant: number;
  className?: string;
};

const ACCENTS: Record<RecipeAccent, { base: string; deep: string; light: string }> = {
  plume: { base: '#12758c', deep: '#0a4453', light: '#7fd0e0' },
  tail: { base: '#119960', deep: '#084d33', light: '#6fd3a4' },
  bronze: { base: '#a87e3b', deep: '#4d3419', light: '#d4b980' },
  gold: { base: '#c9a227', deep: '#6b4a26', light: '#e9d484' },
};

/** Feather-eye motif reused across the illustration variants. */
function FeatherEye({
  x,
  y,
  scale,
  colors,
  rotate = 0,
}: {
  x: number;
  y: number;
  scale: number;
  colors: { base: string; deep: string; light: string };
  rotate?: number;
}) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`}>
      <ellipse cx="0" cy="0" rx="16" ry="20" fill={colors.base} opacity="0.9" />
      <ellipse cx="0" cy="2" rx="11" ry="14" fill={colors.light} opacity="0.9" />
      <ellipse cx="0" cy="4" rx="7" ry="9" fill={colors.deep} />
      <ellipse cx="-2.5" cy="-3" rx="4" ry="5" fill="#ffffff" opacity="0.16" />
    </g>
  );
}

/**
 * Per-recipe cover art: a glossy plated omelette against an accent-colored
 * iridescent field, with feather-eye motifs arranged by variant. Purely
 * decorative; always rendered aria-hidden with the recipe title adjacent.
 */
export function RecipeIllustration({ accent, variant, className }: RecipeIllustrationProps) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const colors = ACCENTS[accent];
  const v = ((variant % 5) + 5) % 5;

  const eyeLayouts: [number, number, number, number][][] = [
    // [x, y, scale, rotate] per eye
    [
      [52, 40, 1.1, -15],
      [252, 130, 0.8, 20],
    ],
    [
      [40, 130, 0.9, 12],
      [262, 38, 1.0, -18],
      [236, 148, 0.55, 30],
    ],
    [
      [258, 42, 1.2, 14],
      [48, 142, 0.7, -25],
    ],
    [
      [44, 44, 0.8, -30],
      [66, 140, 0.55, 10],
      [256, 120, 1.0, 22],
    ],
    [
      [250, 46, 0.9, 25],
      [42, 96, 1.05, -12],
    ],
  ];

  return (
    <svg viewBox="0 0 300 180" className={className} aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={`${uid}-bg`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={colors.deep} />
          <stop offset="55%" stopColor={colors.base} />
          <stop offset="100%" stopColor={colors.deep} />
        </linearGradient>
        <radialGradient id={`${uid}-plate`} cx="0.4" cy="0.35" r="0.75">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="75%" stopColor="#f1ede2" />
          <stop offset="100%" stopColor="#ddd5c2" />
        </radialGradient>
        <linearGradient id={`${uid}-egg`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffe9a8" />
          <stop offset="60%" stopColor="#f5c542" />
          <stop offset="100%" stopColor="#dfa32b" />
        </linearGradient>
        <linearGradient id={`${uid}-sheen`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
      </defs>

      <rect width="300" height="180" fill={`url(#${uid}-bg)`} />
      {eyeLayouts[v].map(([x, y, s, r], i) => (
        <FeatherEye key={i} x={x} y={y} scale={s} rotate={r} colors={colors} />
      ))}

      {/* Plate */}
      <ellipse cx="150" cy="108" rx="86" ry="46" fill="rgba(4,31,39,0.28)" />
      <ellipse cx="150" cy="100" rx="86" ry="46" fill={`url(#${uid}-plate)`} />
      <ellipse cx="150" cy="100" rx="66" ry="34" fill="none" stroke="#d8cfba" strokeWidth="1.5" />

      {/* Folded omelette */}
      <path
        d="M92 104 Q150 62 208 104 Q150 128 92 104 Z"
        fill={`url(#${uid}-egg)`}
        stroke="#c98f1f"
        strokeWidth="1.5"
      />
      <path
        d="M100 102 Q150 72 200 102"
        fill="none"
        stroke="#fff3cd"
        strokeWidth="3"
        opacity="0.7"
      />

      {/* Garnish */}
      <circle cx="120" cy="112" r="3" fill={colors.base} />
      <circle cx="182" cy="114" r="2.5" fill={colors.base} />
      <circle cx="150" cy="118" r="2" fill={colors.light} />

      {/* Gloss over the whole scene */}
      <rect width="300" height="90" fill={`url(#${uid}-sheen)`} />
    </svg>
  );
}
